'use client';

import React, { useEffect, useRef, useState } from 'react';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.scroll-reveal-init');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Fehler beim Senden der Anfrage.');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ein unbekannter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--background)',
    border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) + 8px)',
    fontSize: '0.875rem',
    color: 'var(--foreground)',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  return (
    <section id="kontakt" className="py-16 md:py-32 px-5 md:px-10" style={{ backgroundColor: 'rgba(232,228,223,0.4)' }} ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left Info */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-10">
            {/* Header */}
            <div className="scroll-reveal-init">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] mb-6" style={{ color: 'var(--accent)' }}>
                <span className="gold-dot" />
                Kontakt
              </span>
              <h2 className="font-display text-hero-sm font-semibold mb-6 text-balance" style={{ color: 'var(--foreground)' }}>
                Lassen Sie uns{' '}
                <span className="italic">sprechen</span>
              </h2>
              <p className="text-base leading-relaxed font-light" style={{ color: 'var(--muted-foreground)' }}>
                Schildern Sie uns Ihre Situation — wir melden uns innerhalb von 24 Stunden für ein unverbindliches Erstgespräch.
              </p>
            </div>

            {/* Contact Info */}
            <div className="scroll-reveal-init stagger-2 space-y-4">
              {[
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  label: 'E-Mail',
                  value: 'info@aktas-buchhaltung.de',
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  ),
                  label: 'Telefon',
                  value: '+49 1522 3610151',
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  label: 'Adresse',
                  value: 'Langstraße 6, 65589 Hadamar, Deutschland',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4 py-1">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border"
                    style={{ backgroundColor: 'var(--secondary)', color: 'var(--foreground)', borderColor: 'var(--border)' }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: 'var(--muted-foreground)' }}>{item.label}</p>
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Office Hours */}
            <div className="scroll-reveal-init stagger-3 rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: 'var(--muted-foreground)' }}>Bürozeiten</p>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium" style={{ color: 'var(--muted-foreground)' }}>Montag – Freitag</span>
                  <span className="font-semibold" style={{ color: 'var(--foreground)' }}>10:00 – 18:00 Uhr</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium" style={{ color: 'var(--muted-foreground)' }}>Samstag</span>
                  <span className="font-semibold" style={{ color: 'var(--foreground)' }}>Nach Vereinbarung</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7 scroll-reveal-init stagger-2">
            <div
              className="rounded-3xl p-6 sm:p-8 md:p-10 border shadow-lg shadow-black/5"
              style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Form Header */}
                <div className="mb-8">
                  <h3 className="font-display text-2xl font-semibold mb-1" style={{ color: 'var(--foreground)' }}>Anfrage senden</h3>
                  <p className="text-sm font-light" style={{ color: 'var(--muted-foreground)' }}>Unverbindlich und kostenlos — wir freuen uns auf Ihre Nachricht.</p>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--muted-foreground)' }}>Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="Ihr vollständiger Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 2px rgba(200,150,90,0.2)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--muted-foreground)' }}>E-Mail *</label>
                    <input
                      type="email"
                      required
                      placeholder="ihre@email.de"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 2px rgba(200,150,90,0.2)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>

                {/* Phone + Service */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--muted-foreground)' }}>Telefon</label>
                    <input
                      type="tel"
                      placeholder="+49 ..."
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 2px rgba(200,150,90,0.2)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--muted-foreground)' }}>Gewünschte Leistung</label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      style={{ ...inputStyle, appearance: 'none' }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 2px rgba(200,150,90,0.2)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="Finanzbuchhaltung">Finanzbuchhaltung</option>
                      <option value="Lohnbuchhaltung">Lohnbuchhaltung</option>
                      <option value="Jahresabschluss">Jahresabschluss</option>
                      <option value="Einkommensteuererklärung">Einkommensteuererklärung</option>
                      <option value="Büroservice & Organisation">Büroservice &amp; Organisation</option>
                      <option value="Lexoffice-Unterstützung">Lexoffice-Unterstützung</option>
                      <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--muted-foreground)' }}>Ihre Nachricht *</label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Schildern Sie kurz Ihre Situation und Ihren Beratungsbedarf..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    style={{ ...inputStyle, resize: 'none' }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 2px rgba(200,150,90,0.2)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div className="rounded-xl px-4 py-3 text-sm border" style={{ backgroundColor: 'rgba(220,53,69,0.08)', borderColor: 'rgba(220,53,69,0.3)', color: '#dc3545' }}>
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || submitted}
                  className="w-full py-4 px-8 font-semibold text-sm rounded-full transition-all duration-300 hover:scale-[1.01] flex items-center justify-center gap-2 group min-h-[52px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ backgroundColor: submitted ? 'var(--accent)' : 'var(--primary)', color: 'var(--primary-foreground)' }}
                  onMouseEnter={(e) => { if (!submitted && !loading) e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
                  onMouseLeave={(e) => { if (!submitted && !loading) e.currentTarget.style.backgroundColor = 'var(--primary)'; }}
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Wird gesendet...
                    </>
                  ) : submitted ? (
                    'Anfrage gesendet ✓'
                  ) : (
                    <>
                      Anfrage absenden
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </>
                  )}
                </button>

                <p className="text-xs text-center font-light" style={{ color: 'var(--muted-foreground)' }}>
                  Ihre Daten werden vertraulich behandelt und nicht weitergegeben.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
